import { FLOOR_PLAN_MAX_ROOM_POINTS, FLOOR_PLAN_MAX_ROOMS } from './schemas/floor-plan.js';
/**
 * Комнаты планировки не рисуются вручную, а выводятся из стен: каждый
 * замкнутый контур — комната. Алгоритм:
 *  1. стены режутся в точках пересечений и Т-стыков (конец одной стены на
 *     середине другой) — получается планарный граф;
 *  2. висячие отрезки (тупики) отбрасываются — они ничего не ограничивают;
 *  3. обход граней графа: у каждой ограниченной грани положительная площадь,
 *     внешняя грань каждой компоненты — отрицательная;
 *  4. контур грани (по осям стен) сдвигается внутрь на половину толщины
 *     каждой стены — площадь получается «по полу», как её считают при ремонте.
 *     У границы зоны (divider) толщина 0: она делит пространство, но пол не занимает.
 *
 * Модуль чистый, без DOM — его используют редактор webapp, шаблоны и
 * (в будущем) мобильный клиент.
 */
/** Точка ближе этого расстояния к стене считается лежащей на ней (Т-стык), см. */
const ON_SEGMENT_TOLERANCE_CM = 1;
/** Грани меньше — артефакты (щель между почти совпадающими стенами), а не комнаты. */
const MIN_ROOM_AREA_M2 = 0.05;
const EPS = 1e-9;
const key = (p) => `${p.x},${p.y}`;
function cross(ax, ay, bx, by) {
    return ax * by - ay * bx;
}
export function floorPlanPointInPolygon(p, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i];
        const b = poly[j];
        if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
            inside = !inside;
        }
    }
    return inside;
}
/** Знаковая площадь в см² (формула шнурования). */
function signedArea(poly) {
    let sum = 0;
    for (let i = 0; i < poly.length; i++) {
        const p = poly[i];
        const q = poly[(i + 1) % poly.length];
        sum += p.x * q.y - q.x * p.y;
    }
    return sum / 2;
}
/** Центр масс многоугольника; для вогнутых, где он снаружи, — среднее вершин. */
export function floorPlanPolygonCentroid(poly) {
    const area = signedArea(poly);
    const mean = {
        x: poly.reduce((s, p) => s + p.x, 0) / (poly.length || 1),
        y: poly.reduce((s, p) => s + p.y, 0) / (poly.length || 1),
    };
    if (Math.abs(area) < EPS)
        return mean;
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < poly.length; i++) {
        const p = poly[i];
        const q = poly[(i + 1) % poly.length];
        const c = p.x * q.y - q.x * p.y;
        cx += (p.x + q.x) * c;
        cy += (p.y + q.y) * c;
    }
    const centroid = { x: cx / (6 * area), y: cy / (6 * area) };
    return floorPlanPointInPolygon(centroid, poly) ? centroid : mean;
}
/**
 * Гарантированно внутренняя точка многоугольника — для сопоставления новых
 * контуров со старыми комнатами (у Г-образной комнаты центр масс снаружи).
 */
function interiorPoint(poly) {
    const c = floorPlanPolygonCentroid(poly);
    if (floorPlanPointInPolygon(c, poly))
        return c;
    const sign = signedArea(poly) >= 0 ? 1 : -1;
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i];
        const b = poly[(i + 1) % poly.length];
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < EPS)
            continue;
        const nx = (-(b.y - a.y) / len) * sign;
        const ny = ((b.x - a.x) / len) * sign;
        const p = { x: (a.x + b.x) / 2 + nx * 2, y: (a.y + b.y) / 2 + ny * 2 };
        if (floorPlanPointInPolygon(p, poly))
            return p;
    }
    return c;
}
/** Параметры t (0..1) на отрезке s, в которых его надо разрезать. */
function splitParams(s, others) {
    const dx = s.b.x - s.a.x;
    const dy = s.b.y - s.a.y;
    const len2 = dx * dx + dy * dy;
    const len = Math.sqrt(len2);
    const ts = [0, 1];
    const addIfOnSegment = (p) => {
        const t = ((p.x - s.a.x) * dx + (p.y - s.a.y) * dy) / len2;
        if (t <= EPS || t >= 1 - EPS)
            return;
        const dist = Math.abs(cross(dx, dy, p.x - s.a.x, p.y - s.a.y)) / len;
        if (dist <= ON_SEGMENT_TOLERANCE_CM)
            ts.push(t);
    };
    for (const o of others) {
        if (o === s)
            continue;
        // Т-стыки и наложения: концы другой стены на этой.
        addIfOnSegment(o.a);
        addIfOnSegment(o.b);
        // Пересечение «крестом».
        const ex = o.b.x - o.a.x;
        const ey = o.b.y - o.a.y;
        const denom = cross(dx, dy, ex, ey);
        if (Math.abs(denom) < EPS)
            continue;
        const t = cross(o.a.x - s.a.x, o.a.y - s.a.y, ex, ey) / denom;
        const u = cross(o.a.x - s.a.x, o.a.y - s.a.y, dx, dy) / denom;
        if (t > EPS && t < 1 - EPS && u > -EPS && u < 1 + EPS)
            ts.push(t);
    }
    return ts.sort((p, q) => p - q);
}
const edgeKey = (kp, kq) => (kp < kq ? `${kp}|${kq}` : `${kq}|${kp}`);
function buildGraph(walls) {
    const segments = walls
        .filter((w) => w.a.x !== w.b.x || w.a.y !== w.b.y)
        .map((w) => ({ a: w.a, b: w.b, thickness: w.divider ? 0 : w.thickness, railing: Boolean(w.railing) }));
    const points = new Map();
    const adj = new Map();
    const rail = new Set();
    const solid = new Set();
    const link = (p, q, thickness, railing) => {
        const kp = key(p);
        const kq = key(q);
        if (kp === kq)
            return;
        (railing ? rail : solid).add(edgeKey(kp, kq));
        points.set(kp, p);
        points.set(kq, q);
        if (!adj.has(kp))
            adj.set(kp, new Map());
        if (!adj.has(kq))
            adj.set(kq, new Map());
        // Две стены по одному месту — берём более толстую.
        const prev = adj.get(kp).get(kq) ?? 0;
        adj.get(kp).set(kq, Math.max(prev, thickness));
        adj.get(kq).set(kp, Math.max(prev, thickness));
    };
    for (const s of segments) {
        const ts = splitParams(s, segments);
        const pts = ts.map((t) => ({
            x: Math.round(s.a.x + (s.b.x - s.a.x) * t),
            y: Math.round(s.a.y + (s.b.y - s.a.y) * t),
        }));
        for (let i = 0; i + 1 < pts.length; i++)
            link(pts[i], pts[i + 1], s.thickness, s.railing);
    }
    // Тупики ничего не ограничивают — срезаем их, пока есть вершины степени 1.
    const queue = [...adj.keys()].filter((k) => adj.get(k).size === 1);
    while (queue.length > 0) {
        const k = queue.pop();
        const nbrs = adj.get(k);
        if (!nbrs || nbrs.size !== 1)
            continue;
        const [other] = nbrs.keys();
        adj.delete(k);
        const on = adj.get(other);
        if (on) {
            on.delete(k);
            if (on.size === 1)
                queue.push(other);
            if (on.size === 0)
                adj.delete(other);
        }
    }
    return { points, adj, railing: new Set([...rail].filter((e) => !solid.has(e))) };
}
/** Обход граней: следующее ребро — соседнее по углу «перед» обратным ребром. */
function traceFaces(graph) {
    const { points, adj } = graph;
    const sorted = new Map();
    for (const [k, nbrs] of adj) {
        const p = points.get(k);
        sorted.set(k, [...nbrs.keys()].sort((a, b) => {
            const pa = points.get(a);
            const pb = points.get(b);
            return Math.atan2(pa.y - p.y, pa.x - p.x) - Math.atan2(pb.y - p.y, pb.x - p.x);
        }));
    }
    const visited = new Set();
    const faces = [];
    for (const [u, nbrs] of adj) {
        for (const v of nbrs.keys()) {
            if (visited.has(`${u}>${v}`))
                continue;
            const keys = [];
            const thickness = [];
            let outdoor = false;
            let from = u;
            let to = v;
            let guard = 0;
            while (!visited.has(`${from}>${to}`) && guard++ < 10_000) {
                visited.add(`${from}>${to}`);
                keys.push(from);
                thickness.push(adj.get(from).get(to));
                if (graph.railing.has(edgeKey(from, to)))
                    outdoor = true;
                const around = sorted.get(to);
                const idx = around.indexOf(from);
                const next = around[(idx - 1 + around.length) % around.length];
                from = to;
                to = next;
            }
            faces.push({ points: keys.map((k) => points.get(k)), thickness, outdoor });
        }
    }
    return faces;
}
/** Убирает вершины посреди прямой (Т-стык с другой стороны стены), если толщина не меняется. */
function simplify(face) {
    const n = face.points.length;
    const points = [];
    const thickness = [];
    for (let i = 0; i < n; i++) {
        const prev = face.points[(i - 1 + n) % n];
        const p = face.points[i];
        const next = face.points[(i + 1) % n];
        const collinear = Math.abs(cross(p.x - prev.x, p.y - prev.y, next.x - p.x, next.y - p.y)) < EPS;
        if (collinear && face.thickness[(i - 1 + n) % n] === face.thickness[i])
            continue;
        points.push(p);
        thickness.push(face.thickness[i]);
    }
    return { points, thickness, outdoor: face.outdoor };
}
/**
 * Сдвиг контура внутрь: каждое ребро — на половину толщины своей стены.
 * Контур с положительной площадью: внутренняя нормаль ребра d — (-dy, dx).
 */
function inset(face) {
    const { points, thickness } = face;
    const n = points.length;
    const lines = points.map((a, i) => {
        const b = points[(i + 1) % n];
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        const off = thickness[i] / 2;
        const nx = (-(b.y - a.y) / len) * off;
        const ny = ((b.x - a.x) / len) * off;
        return { ax: a.x + nx, ay: a.y + ny, dx: b.x - a.x, dy: b.y - a.y, off };
    });
    const out = [];
    for (let i = 0; i < n; i++) {
        const l1 = lines[(i - 1 + n) % n];
        const l2 = lines[i];
        const denom = cross(l1.dx, l1.dy, l2.dx, l2.dy);
        if (Math.abs(denom) < EPS) {
            // Продолжение по прямой со сменой толщины — ступенька из двух точек.
            const p = points[i];
            const len1 = Math.hypot(l1.dx, l1.dy);
            const len2 = Math.hypot(l2.dx, l2.dy);
            out.push({ x: p.x - (l1.dy / len1) * l1.off, y: p.y + (l1.dx / len1) * l1.off });
            out.push({ x: p.x - (l2.dy / len2) * l2.off, y: p.y + (l2.dx / len2) * l2.off });
            continue;
        }
        const s = cross(l2.ax - l1.ax, l2.ay - l1.ay, l2.dx, l2.dy) / denom;
        out.push({ x: l1.ax + l1.dx * s, y: l1.ay + l1.dy * s });
    }
    return out.map((p) => ({ x: Math.round(p.x), y: Math.round(p.y) }));
}
/** Контуры комнат (по полу, без толщины стен) из набора стен. */
export function detectFloorPlanRoomPolygons(walls) {
    const faces = traceFaces(buildGraph(walls));
    const rooms = [];
    for (const raw of faces) {
        if (signedArea(raw.points) / 10_000 < MIN_ROOM_AREA_M2)
            continue; // внешние грани < 0
        const face = simplify(raw);
        if (face.points.length < 3)
            continue;
        let poly = inset(face);
        // Стены толще самой комнаты — сдвиг «вывернул» контур; лучше показать по осям.
        if (signedArea(poly) <= 0)
            poly = face.points;
        if (poly.length > FLOOR_PLAN_MAX_ROOM_POINTS)
            continue;
        rooms.push({ points: poly, outdoor: Boolean(face.outdoor) });
    }
    return rooms.sort((p, q) => signedArea(q.points) - signedArea(p.points)).slice(0, FLOOR_PLAN_MAX_ROOMS);
}
function randomId() {
    return Math.random().toString(36).slice(2, 10);
}
/**
 * Пересчитывает комнаты из стен, сохраняя имена и id: новый контур наследует
 * комнату, в которую попадает его внутренняя точка (или которая попадает в
 * него). Если стену поставили посреди комнаты, имя остаётся у большей части.
 */
export function syncFloorPlanRooms(data, previous, defaultName) {
    const detected = detectFloorPlanRoomPolygons(data.walls);
    const used = new Set();
    const matched = [];
    const fresh = [];
    for (const d of detected) {
        const poly = d.points;
        const inner = interiorPoint(poly);
        const idx = previous.findIndex((r) => !used.has(r.id) &&
            (floorPlanPointInPolygon(inner, r.points) || floorPlanPointInPolygon(interiorPoint(r.points), poly)));
        if (idx >= 0) {
            used.add(previous[idx].id);
            // Признак «балкон» всегда берём из стен: убрали ограждение — комната обычная.
            matched.push({ order: idx, room: { ...previous[idx], points: poly, outdoor: d.outdoor ? true : undefined } });
        }
        else {
            fresh.push(d);
        }
    }
    const rooms = matched.sort((a, b) => a.order - b.order).map((m) => m.room);
    const names = new Set(rooms.map((r) => r.name));
    let n = 1;
    for (const d of fresh) {
        while (names.has(defaultName(n, d.outdoor)))
            n++;
        const name = defaultName(n, d.outdoor);
        names.add(name);
        rooms.push({ id: randomId(), name, points: d.points, ...(d.outdoor ? { outdoor: true } : {}) });
    }
    return rooms;
}
//# sourceMappingURL=floor-plan-geometry.js.map